import React from 'react';
import './MoodDistribution.css';

const MOOD_COLORS = {
  overwhelmed: '#EF4444',
  sad: '#8B5CF6',
  anxious: '#F59E0B',
  neutral: '#6B7280',
  calm: '#3B82F6',
  happy: '#10B981'
};

function MoodDistribution({ data }) {
  if (!data || !data.overall) return null;

  const { overall, byDayOfWeek, byTimeOfDay } = data;

  // Convert overall counts to percentage format for display
  const totalEntries = Object.values(overall).reduce((sum, count) => sum + count, 0);
  const sortedMoods = Object.entries(overall)
    .map(([mood, count]) => ({
      mood,
      count,
      percentage: totalEntries > 0 ? (count / totalEntries) * 100 : 0
    }))
    .sort((a, b) => b.percentage - a.percentage);

  // Days of week in order
  const daysOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="mood-distribution">
      <h3 className="mood-distribution__title">Mood Patterns & Distribution</h3>

      {/* Overall Distribution */}
      <section className="mood-distribution__section">
        <h4 className="mood-distribution__subtitle">Overall Distribution</h4>
        <div className="mood-distribution__bars">
          {sortedMoods.map(({ mood, count, percentage }) => (
            <div key={mood} className="mood-distribution__bar-container">
              <div className="mood-distribution__bar-header">
                <span className="mood-distribution__bar-label">
                  {mood.charAt(0).toUpperCase() + mood.slice(1)}
                </span>
                <span className="mood-distribution__bar-stats">
                  {count} ({percentage.toFixed(1)}%)
                </span>
              </div>
              <div className="mood-distribution__bar-track">
                <div
                  className="mood-distribution__bar-fill"
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: MOOD_COLORS[mood] || '#6B7280'
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Day of Week Pattern */}
      {byDayOfWeek && Object.keys(byDayOfWeek).length > 0 && (
        <section className="mood-distribution__section">
          <h4 className="mood-distribution__subtitle">Day of Week Patterns</h4>
          <div className="mood-distribution__heatmap">
            {daysOrder.map(day => {
              const dayData = byDayOfWeek[day];
              if (!dayData) return null;
              
              const intensity = ((dayData.averageScore || 1) - 1) / 5;
              const color = MOOD_COLORS[dayData.dominantMood] || '#6B7280';
              
              return (
                <div key={day} className="mood-distribution__day-card">
                  <div className="mood-distribution__day-name">{day.slice(0, 3)}</div>
                  <div
                    className="mood-distribution__day-indicator"
                    style={{
                      backgroundColor: color,
                      opacity: 0.3 + (intensity * 0.7)
                    }}
                  >
                    <span className="mood-distribution__day-score">
                      {(dayData.averageScore || 0).toFixed(1)}
                    </span>
                  </div>
                  <div className="mood-distribution__day-mood">
                    {dayData.dominantMood}
                  </div>
                  <div className="mood-distribution__day-count">
                    {dayData.count || 0} entries
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Time of Day */}
      {byTimeOfDay && Object.keys(byTimeOfDay).length > 0 && (
        <section className="mood-distribution__section">
          <h4 className="mood-distribution__subtitle">Time of Day Patterns</h4>
          <div className="mood-distribution__time-grid">
            {Object.entries(byTimeOfDay).map(([time, timeData]) => (
              <div key={time} className="mood-distribution__time-card">
                <div className="mood-distribution__time-icon">
                  {time === 'morning' && '🌅'}
                  {time === 'afternoon' && '☀️'}
                  {time === 'evening' && '🌙'}
                </div>
                <div className="mood-distribution__time-name">
                  {time.charAt(0).toUpperCase() + time.slice(1)}
                </div>
                <div
                  className="mood-distribution__time-score"
                  style={{ color: MOOD_COLORS[timeData.dominantMood] || '#6B7280' }}
                >
                  {(timeData.averageScore || 0).toFixed(1)}/6
                </div>
                <div className="mood-distribution__time-mood">
                  {timeData.dominantMood}
                </div>
                <div className="mood-distribution__time-count">
                  {timeData.count || 0} entries
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default MoodDistribution;
